<template>
  <!--////////////////////////// TOP HEADER ////////////////////////// -->
  <div class="help-header">
    <div class="container mb-0 py-0">
      <div class="row align-items-center">
        <div :class="{ 'col-4': helpStage === 'DEFAULT' }" class="view-title">
          <img
            :src="`${useAsset('img/credit-line/back-button.png')}`"
            @click="goBack"
            alt="back"
          />
          <span class="ms-0 text-black title-styling" style="font-weight: 600">
            {{ screenTitle }}
          </span>
        </div>
        <div class="col-8 ps-0 pe-3 badge-section">
          <div
            :style="{
              color: '#666666',
              backgroundColor: '#e6f4ea',
              fontWeight: 600,
            }"
            class="float-end px-2 py-1 rounded-1"
          >
            {{
              t("DEVICE_TAG_CL_HELP", {
                deviceTag: deviceTag,
              })
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <hr class="w-100 mx-0 p-0 mt-2" />
  <div
    v-if="displayCioSpaceOn"
    :id="inlineMessageHtmlId"
    class="w-100 mx-auto px-3 d-flex flex-column justify-content-between gap-2"
  ></div>
  <!--////////////////////////// HELP MENU ////////////////////////// -->
  <section class="w-100 px-3" v-if="helpStage === 'DEFAULT'">
    <CollapseCard
      default-open
      fill
      bold-title
      :collapsable="false"
      :title="t('HELP_AND_SUPPORT')"
      :content-style="collapseCardContentStyle"
    >
      <MenuItem
        v-if="showNewUI"
        :text="t('APP_HOME_TUTORIAL')"
        @click="openAppHomeTutorial"
        materialIcon="play_circle"
      />
      <MenuItem text="FAQs" @click="switchHelpPageStage('FAQS')" />
      <MenuItem
        :text="t('LOCKED_FUNCTIONS')"
        @click="switchHelpPageStage('LOCKED_FUNCTIONS')"
        class="last-menu-item"
      />
    </CollapseCard>
    <CollapseCard
      v-if="whatsAppSupportNumber?.enabled || supportChatbot?.enabled"
      default-open
      fill
      bold-title
      :titleDescription="t('CONTACT_US_SUBTITLE')"
      class="mt-3"
      :collapsable="false"
      :title="t('CONTACT_US')"
      :content-style="collapseCardContentStyle"
    >
      <MenuItem
        v-if="whatsAppSupportNumber?.enabled"
        text="Whatsapp"
        @click="openWhatsApp"
        :icon="useAsset('img/credit-line/wp_icon_gray.svg')"
      />
      <MenuItem
        v-if="supportChatbot?.enabled"
        :text="t('ONLINE_CHAT')"
        materialIcon="maps_ugc"
        iconPadding="pr-dotfive"
        class="last-menu-item"
      />
    </CollapseCard>
    <CollapseCard
      default-open
      fill
      bold-title
      class="mt-3"
      :collapsable="false"
      :title="t('CL_DOCUMENTS')"
      :content-style="collapseCardContentStyle"
    >
      <MenuItem
        :text="t('CL_ACCOUNT_STATEMENTS')"
        @click="goToAccountStatements"
      />
      <MenuItem
        :text="t('CL_CONTRACTS')"
        @click="openContractsMenu"
      />
      <MenuItem
        :text="t('PRIVACY_POLICY')"
        v-if="privacyPolicyInHelpPage.enabled"
        @click="openPrivacyPolicy"
      />
      <MenuItem
        :text="t('CL_HELP.TAC')"
        v-if="termsAndConditionsInHelpPage.enabled"
        @click="openTac"
      />
      <MenuItem
        :text="t('CL_HELP.CUSTOMER_CHARTER')"
        v-if="customerCharterInHelpPage?.enabled"
        class="last-menu-item"
        @click="openCharter"
      />
    </CollapseCard>
    <ChatBotSmoochCL
      v-if="supportChatbot?.enabled"
      :initialize-chat-bot="supportChatbot?.enabled"
      :integration-id="(supportChatbot?.values?.integrationId) as string ?? ''"
      :chat-bot-metadata="chatBotMetadata"
      :is-in-store-device="false"
    />
  </section>
  <!--////////////////////////// FAQS, CONTRACTS & LOCKED ////////////////////////// -->
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :alertType="alertType"
    :closable="alertClosable"
    :alertIcon="alertIcon"
    @onClose="dismissAlert"
  />
  <FAQS
    v-if="helpStage === 'FAQS' && country"
    @redirectToCalculator="redirectToCalculator"
    :faq-param="faqParam"
    :country="country"
    @cancel-credit-line="cancelCreditLine"
    :include-cc-faqs="includeCcFaqs"
    :use-faq-settings="useFaqSettings"
    :faq-settings="faqSettings"
    :country-values="countryValues"
  />
  <ContractsMenu
    v-if="helpStage === 'CONTRACTS'"
    :is-loading="isContractsLoading"
    :documents="contractsDocuments"
    @open-document="openContractDocument"
  />
  <LockedFunctions
    @back="goBack"
    @link-settings="linkSettings"
    :link-settings-available="linkSettingsAvailable"
    v-if="helpStage === 'LOCKED_FUNCTIONS'"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, PropType } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useI18n as useGlobalI18n } from "vue-i18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import type { AxiosError } from "axios";
import Alert from "@/shared-components/common/Alert.vue";
import { useRoute, useRouter } from "vue-router";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { getLanguageName } from "@/shared-helpers/language.helper";
import ChatBotSmoochCL from "@/apps/credit-line/components/ChatBotSmoochCL.vue";
import { checkDelinquency } from "@/apps/credit-line/helpers/checkDeliquency.helper";
import CollapseCard from "@/apps/credit-line/components/CollapseCard.vue";
import FAQS from "@/apps/credit-line/components/Help/Faqs.vue";
import MenuItem from "@/apps/credit-line/components/Help/MenuItem.vue";
import LockedFunctions from "@/apps/credit-line/components/Help/LockedFunctions.vue";
import ContractsMenu from "@/apps/credit-line/components/Help/ContractsMenu.vue";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import {
  FeatureControlService,
  AvailableFeatures,
  Country,
} from "@/apps/credit-line/services/feature-control.service";
import { FaqSettings, FaqCountryValues } from "@/apps/credit-line/helpers/settingsFaq.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { SettingValue } from "@/shared-models/consumer/ISetting";
import { Service as ApiService } from "@/apps/credit-line/services/network.service";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
declare const Android: AndroidInterface;
import { cioanalytics } from "@/apps/credit-line/services/customerio.service";
import { LogType } from "@/types/dataDogLogType";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import {
  sanitizeHelpContractDocuments,
} from "@/apps/credit-line/helpers/helpContracts.helper";
import type { HelpContractDocument } from "@/apps/credit-line/helpers/helpContracts.helper";

enum FAQ_URL {
  withdraw = "withdraw",
  card = "card",
  pay = "pay",
  physicalcard = "physical-card",
  virtual_card = "virtual-card",
}
type HelpStages = "DEFAULT" | "FAQS" | "CONTRACTS" | "LOCKED_FUNCTIONS";
type AlertType =
  | "alert-warning"
  | "alert-success"
  | "alert-info"
  | "alert-danger"
  | "alert-info-dark";

const props = defineProps({
  whatsAppSupportNumber: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  customerCharterInHelpPage: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  termsAndConditionsInHelpPage: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  privacyPolicyInHelpPage: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  supportChatbot: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  useFaqSettings: {
    type: Boolean,
    default: false,
  },
  faqSettings: {
    type: Object as PropType<FaqSettings | null>,
    default: null,
  },
  countryValues: {
    type: Object as PropType<FaqCountryValues | null>,
    default: null,
  },
});

const { t } = useI18n();
const { t: globalT } = useGlobalI18n();
const route = useRoute();
const alertClosable = ref<boolean>(true);
const screenTitle = ref<string>(t("HELP"));
const userId = ref<number>();
const creditLineId = ref<number>();
const availableCredit = ref<number>();
const customerName = ref<string>();
const customerBirthday = ref<string>();
const customerIdNumber = ref<string>();
const country = ref<CL_COUNTRY | null>();
const phone = ref<string | null>();
const alertType = ref<AlertType>("alert-danger");
const alertIcon = ref<string>("fa fa-exclamation-triangle");
const alertMessage = ref<string>("");
const showAlert = ref<boolean>(false);
const deviceTag = ref<string>(AuthenticationHelper.getDeviceTag());
const creditLineClosable = ref<boolean>(false);
const helpStage = ref<HelpStages>("DEFAULT");
const includeCcFaqs = ref<boolean>(false);
const linkSettingsAvailable = ref<boolean>(false);
const isDelinquent = ref<boolean>(false);
const contractsDocuments = ref<HelpContractDocument[]>([]);
const isContractsLoading = ref<boolean>(false);
const hasFetchedContracts = ref<boolean>(false);
const FeatureControl = new FeatureControlService();
const { fetchSummary } = useCreditLineSummary();
const apiService = new ApiService();
const showNewUI = Android.shouldDisplayNewUi();
const router = useRouter();

const getLocalizedTextOrFallback = (
  translationKey: string,
  fallbackValue: string
): string => {
  const appTranslationKey = `CREDIT_LINE.${translationKey}`;
  const appTranslationValue = globalT(appTranslationKey);

  if (appTranslationValue !== appTranslationKey) {
    return appTranslationValue;
  }

  const sharedTranslationKey = `SHARED.${translationKey}`;
  const sharedTranslationValue = globalT(sharedTranslationKey);

  if (sharedTranslationValue !== sharedTranslationKey) {
    return sharedTranslationValue;
  }

  return fallbackValue;
};

const collapseCardContentStyle = computed(() => ({
  padding: "0px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  alignItems: "center",
}));

const faqParam = computed(() => {
  if (route.query?.faq) {
    return route.query?.faq as string;
  } else {
    return undefined;
  }
});

// todo: Move the list of supported help pages for customerIo to configurability
const displayCioSpaceOn =
  ["card"].includes(faqParam.value ?? "") && !!deviceTag.value;

const inlineMessageHtmlId = faqParam.value
  ? "help-inline-message-container-" + faqParam.value
  : "help-inline-message-container";

const dismissAlert = () => (showAlert.value = false);

const openAppHomeTutorial = () =>
  router.push({ name: "OnboardingSliderView", query: { fromHelp: 1 } });

const switchHelpPageStage = (stage: HelpStages) => {
  switch (stage) {
    case "FAQS":
      screenTitle.value = t("FAQ");
      break;
    case "CONTRACTS":
      screenTitle.value = t("CL_CONTRACTS");
      break;
    case "LOCKED_FUNCTIONS":
      screenTitle.value = t("LOCKED_FUNCTIONS");
      break;
    case "DEFAULT":
    default:
      screenTitle.value = t("HELP");
      break;
  }
  helpStage.value = stage;
};

const getSummary = () => {
  fetchSummary()
    .then((summary) => {
      if (summary) {
        includeCcFaqs.value = FeatureControl.isFeatureEnabled(
          AvailableFeatures.PJ_CARD_FAQS,
          summary.country as Country
        );
        creditLineId.value = summary.creditLineId;
        userId.value = summary.userId;
        availableCredit.value = summary.availableCredit;
        customerName.value = summary.customerName;
        customerBirthday.value = summary.user.birthDate;
        customerIdNumber.value = summary.user.idNumber;
        country.value = summary.country as CL_COUNTRY;
        phone.value = summary.customerPhone;
        isDelinquent.value = checkDelinquency(summary);
        creditLineClosable.value = checkClosable(summary);
      }
    })
    .catch((err) => {
      console.error(err);
    })
    .finally(() => {
      if (isAndroid()) {
        Android.finishedLoading();
      }
    });
};

const checkClosable = (summary) => {
  if (summary.payoffBalanceToday > 0) {
    return false;
  }
  const pendingWithdrawal = (summary.withdrawals = summary.withdrawals.find(
    (e) => e.status === "READY" || e.status === "PENDING"
  ));
  return !pendingWithdrawal;
};

const cancelCreditLine = () => {
  if (!creditLineClosable.value) {
    alertType.value = "alert-danger";
    alertMessage.value = t("CANNOTCANCEL");
    alertIcon.value = "fa fa-exclamation-triangle";
    showAlert.value = true;
    window.scrollTo(0, 0);
  } else {
    router.push({ name: "CreditLineCancelView" });
  }
};

const linkSettings = () => Android.openSettings();

const goToAccountStatements = () =>
  router.push({
    name: "HistoricalBalancesView",
    query: { isDelinquent: String(isDelinquent.value) },
  });

const fetchAvailableContractsDocuments = async () => {
  if (hasFetchedContracts.value || isContractsLoading.value) {
    return;
  }

  isContractsLoading.value = true;

  try {
    const response = await apiService.fetchAvailableDocuments({
      deviceTag: deviceTag.value,
    });
    contractsDocuments.value = sanitizeHelpContractDocuments(
      response.data.documents
    );
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error as AxiosError);
    contractsDocuments.value = [];
  } finally {
    hasFetchedContracts.value = true;
    isContractsLoading.value = false;
  }
};

const openContractsMenu = async () => {
  switchHelpPageStage("CONTRACTS");
  await fetchAvailableContractsDocuments();
};

const openContractDocument = (document: HelpContractDocument) => {
  if (isDelinquent.value) {
    alertType.value = "alert-danger";
    alertMessage.value = t("DELINQUENT_ALERT");
    alertIcon.value = "fa fa-exclamation-triangle";
    showAlert.value = true;
    return;
  }

  if (
    isAndroid() &&
    Android &&
    typeof Android.downloadStatement === "function"
  ) {
    Android.downloadStatement(
      document.base64Document,
      getLocalizedTextOrFallback(document.key, document.key)
    );
    return;
  }

  window.open(
    `data:application/pdf;base64,${document.base64Document}`,
    "_blank"
  );
};

const openWhatsApp = async () => {
  const helpMsg = t("CL_WP_HELP_MSG", {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });
  if (
    props.whatsAppSupportNumber.enabled &&
    props.whatsAppSupportNumber.values?.phone
  ) {
    window.open(
      `https://wa.me/${props.whatsAppSupportNumber.values.phone}?text="${helpMsg}`,
      "_system"
    );
  }
};

const goBack = () => {
  if (
    helpStage.value === "DEFAULT" ||
    Object.values(FAQ_URL).includes(route.query?.faq as FAQ_URL)
  ) {
    if (history.length > 1) {
      router.back();
      return;
    }
    Android.dismissWebView();
  } else {
    switchHelpPageStage("DEFAULT");
  }
};

const redirectToCalculator = () => {
  return router.push({
    name: "CreditCardTransactionCalculator",
    query: {
      fromHelp: 1,
      origin: "withdrawal_help_page",
      creditLineId: creditLineId.value,
      userId: userId.value,
      availableCredit: availableCredit.value,
    },
  });
};

const openPrivacyPolicy = () => {
  if (props.privacyPolicyInHelpPage?.enabled) {
    const url = props.privacyPolicyInHelpPage.values?.url;
    if (typeof url === "string") {
      window.open(url, "_system");
    }
  }
};

const openTac = () => {
  if (props.termsAndConditionsInHelpPage?.enabled) {
    const url = props.termsAndConditionsInHelpPage.values?.url;
    if (typeof url === "string") {
      window.open(url, "_system");
    }
  }
};

const openCharter = () => {
  if (props.customerCharterInHelpPage?.enabled) {
    const url = props.customerCharterInHelpPage.values?.url;
    if (typeof url === "string") {
      window.open(url, "_system");
    }
  }
};

const chatBotMetadata = computed(() => ({
  name: customerName.value,
  userType: "customer",
  language: getLanguageName(),
  userId: userId.value,
  deviceTag: deviceTag.value,
  country: country.value ?? null,
  phone: phone.value,
  product: "CL",
  birthday: customerBirthday.value,
  idNumber: customerIdNumber.value,
}));

onBeforeMount(() => {
  if (displayCioSpaceOn) {
    DatadogLogger.Log(
      "CustomerIo identify called on Help screen",
      {
        space: faqParam.value,
        deviceTag: deviceTag.value,
      },
      LogType.INFO
    );

    cioanalytics.identify(deviceTag.value);
    cioanalytics.track("creditline help screen " + faqParam.value);
  }
});

onMounted(() => {
  if (
    route.query?.faq &&
    Object.values(FAQ_URL).includes(route.query?.faq as FAQ_URL)
  ) {
    helpStage.value = "FAQS";
  }
  linkSettingsAvailable.value =
    Android && typeof Android.openSettings === "function";
  getSummary();
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";

.download-contract-btn {
  text-decoration: none;
}

.link-no-underline {
  text-decoration: none;
}

.device-tag-pill {
  border-radius: 25px;
  padding: 2%;
  background-color: #ddf3ed;
  font-weight: bolder;
}

.help-header {
  background-color: $pj-color-white;
  padding: 0;
  @media screen and (max-width: $mobile) {
  }

  & + hr + section {
    margin-top: 2rem;
  }
}

.last-menu-item {
  border-radius: 0 0 0.625rem 0.625rem;
}

.view-title {
  text-overflow: ellipsis;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.badge-section {
  flex: 1;
}

.title-styling {
  text-overflow: ellipsis;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.contact-description {
  font-family: Inter, serif;
  color: #8B8B8B;
  font-size: 13px;
  font-weight: 400;
  line-height: 17px;
  padding: 0.5rem 0.69rem 1rem 0.69rem;
  width: 100%;
}
</style>
